/**
 * Helper function for pricing visibility settings
 * Contexts where pricing can be displayed in the application
 */
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientLocation } from "api-types/locations/locationsResponses";

export type PricingContext =
  | "communityPlans"
  | "internalHomePlanLinks"
  | "publicHomePlanLinks"
  | "optionSummary"
  | "salesBrochure"
  | "publicSalesMap";

/**
 * Determines whether pricing should be displayed for a given context
 * following the precedence rules:
 *
 * 1. Location-specific granular settings (highest precedence)
 *    If location has pricingVisibilitySettings and enabled=true,
 *    use the specific context setting from there
 *
 * 2. Ancestor location settings (in hierarchical order)
 *    Checks each ancestor in the array (parent, grandparent, etc.)
 *    If any ancestor has pricingVisibilitySettings and enabled=true,
 *    use the specific context setting from there
 *
 * 3. Builder-level global settings
 *    If builder has pricingVisibilitySettings and enabled=true,
 *    use the specific context setting from there
 *
 * 4. Default to false (safest option)
 *    If no applicable settings are found, don't show prices
 *
 * @param context - The specific pricing context to check (where prices might appear)
 * @param location - The current location object
 * @param ancestors - Array of ancestor locations in hierarchical order (parent, grandparent, etc.)
 * @param builder - The builder object
 * @returns boolean - Whether prices should be displayed for the given context
 */

export function shouldDisplayPricing(
  context: PricingContext,
  location?: ClientLocation | null,
  ancestors?: ClientLocation[] | null,
  builder?: ClientBuilder,
): boolean {
  // Check location-level granular settings(highest precedence)
  if (location?.pricingVisibilitySettings?.[context] !== undefined) {
    return location.pricingVisibilitySettings[context];
  }

  // Check ancestor location settings in hierarchical order (parent, grandparent, etc.)
  // Only use an ancestor's value if that specific context field is defined (allows partial overrides)
  for (const ancestor of ancestors ?? []) {
    if (ancestor.pricingVisibilitySettings?.[context] !== undefined) {
      return ancestor.pricingVisibilitySettings[context];
    }
  }

  // Builder-level settings if no location-specific settings are found
  if (builder?.pricingVisibilitySettings?.[context] !== undefined) {
    return builder.pricingVisibilitySettings[context];
  }
  // Default to false if no settings are found
  return false;
}
